import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Layout } from '../../components/Layout'
import { hasAnyRole } from '../../utils/permissions'
import { supabase } from '../../services/supabase'
import { Plus, Edit, Trash2, X, Save, Tag, CheckSquare, Square, Trash, Power, PowerOff } from 'lucide-react'
import { logActivity } from '../../utils/activityLogger'

const AdminNotificationCategories = () => {
  const { userProfile, loading: authLoading } = useAuth()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    color: '#3B82F6',
    is_active: true,
  })
  const [selectedCategories, setSelectedCategories] = useState([])

  useEffect(() => {
    if (userProfile) {
      fetchData()
    }
  }, [userProfile])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile || !hasAnyRole(userProfile, ['super_admin', 'admin'])) {
    return <Navigate to="/unauthorized" replace />
  }

  const fetchData = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_categories')
        .select('*')
        .order('display_name')

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error)
      alert('Kategoriler yüklenirken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingCategory(null)
    setFormData({
      name: '',
      display_name: '',
      description: '',
      color: '#3B82F6',
      is_active: true,
    })
    setShowModal(true)
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      display_name: category.display_name,
      description: category.description || '',
      color: category.color || '#3B82F6',
      is_active: category.is_active,
    })
    setShowModal(true)
  }

  const handleDelete = async (categoryId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return

    try {
      const { error } = await supabase
        .from('notification_categories')
        .delete()
        .eq('id', categoryId)

      if (error) throw error

      // Activity log
      await logActivity(
        userProfile.id,
        'notification_category_deleted',
        'notification_category',
        categoryId,
        `Bildirim kategorisi silindi: ${categories.find(c => c.id === categoryId)?.display_name}`
      )

      setSelectedCategories([])
      await fetchData()
    } catch (error) {
      alert('Silme işlemi başarısız: ' + error.message)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedCategories.length === 0) {
      alert('Lütfen silmek için en az bir kategori seçin')
      return
    }

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`${selectedCategories.length} kategoriyi silmek istediğinize emin misiniz?`)) return

    try {
      const { error } = await supabase
        .from('notification_categories')
        .delete()
        .in('id', selectedCategories)

      if (error) throw error

      // Activity log
      for (const categoryId of selectedCategories) {
        const category = categories.find(c => c.id === categoryId)
        await logActivity(
          userProfile.id,
          'notification_category_deleted',
          'notification_category',
          categoryId,
          `Bildirim kategorisi silindi: ${category?.display_name}`
        )
      }

      setSelectedCategories([])
      await fetchData()
      alert(`${selectedCategories.length} kategori başarıyla silindi`)
    } catch (error) {
      alert('Toplu silme işlemi başarısız: ' + error.message)
    }
  }

  const handleBulkToggleActive = async (isActive) => {
    if (selectedCategories.length === 0) {
      alert('Lütfen işlem yapmak için en az bir kategori seçin')
      return
    }

    try {
      const { error } = await supabase
        .from('notification_categories')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .in('id', selectedCategories)

      if (error) throw error

      // Activity log
      for (const categoryId of selectedCategories) {
        const category = categories.find(c => c.id === categoryId)
        await logActivity(
          userProfile.id,
          'notification_category_updated',
          'notification_category',
          categoryId,
          `Bildirim kategorisi ${isActive ? 'aktif' : 'pasif'} yapıldı: ${category?.display_name}`
        )
      }

      setSelectedCategories([])
      await fetchData()
      alert(`${selectedCategories.length} kategori ${isActive ? 'aktif' : 'pasif'} yapıldı`)
    } catch (error) {
      alert('Toplu işlem başarısız: ' + error.message)
    }
  }

  const toggleSelectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(categories.map(c => c.id))
    }
  }

  const toggleSelectCategory = (categoryId) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId))
    } else {
      setSelectedCategories([...selectedCategories, categoryId])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (editingCategory) {
        // Güncelleme
        const { error } = await supabase
          .from('notification_categories')
          .update({
            display_name: formData.display_name,
            description: formData.description,
            color: formData.color,
            is_active: formData.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingCategory.id)

        if (error) throw error

        // Activity log
        await logActivity(
          userProfile.id,
          'notification_category_updated',
          'notification_category',
          editingCategory.id,
          `Bildirim kategorisi güncellendi: ${formData.display_name}`
        )
      } else {
        // Yeni kategori
        const { data, error } = await supabase
          .from('notification_categories')
          .insert({
            name: formData.name.toLowerCase().replace(/\s+/g, '_'),
            display_name: formData.display_name,
            description: formData.description,
            color: formData.color,
            is_active: formData.is_active,
          })
          .select()
          .single()

        if (error) throw error

        // Activity log
        await logActivity(
          userProfile.id,
          'notification_category_created',
          'notification_category',
          data.id,
          `Yeni bildirim kategorisi oluşturuldu: ${formData.display_name}`
        )
      }

      setShowModal(false)
      await fetchData()
    } catch (error) {
      alert('İşlem başarısız: ' + error.message)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Yükleniyor...</div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bildirim Kategori Yönetimi</h1>
        <p className="text-gray-600 mt-1">Bildirim kategorilerini yönetin</p>
      </div>

      <div className="flex justify-between items-center mb-6">
        {selectedCategories.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBulkToggleActive(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-md transition-all duration-200"
            >
              <Power size={18} />
              <span>Aktif Yap ({selectedCategories.length})</span>
            </button>
            <button
              onClick={() => handleBulkToggleActive(false)}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 shadow-md transition-all duration-200"
            >
              <PowerOff size={18} />
              <span>Pasif Yap ({selectedCategories.length})</span>
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-md transition-all duration-200"
            >
              <Trash size={18} />
              <span>Sil ({selectedCategories.length})</span>
            </button>
          </div>
        )}
        <button
          onClick={handleCreate}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <Plus size={20} />
          <span>Yeni Kategori</span>
        </button>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center justify-center"
                  title={selectedCategories.length === categories.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                >
                  {selectedCategories.length === categories.length && categories.length > 0 ? (
                    <CheckSquare size={20} className="text-blue-600" />
                  ) : (
                    <Square size={20} className="text-gray-400" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kategori
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Görünen Ad
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Renk
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Durum
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map((category) => (
              <tr key={category.id} className={selectedCategories.includes(category.id) ? 'bg-blue-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => toggleSelectCategory(category.id)}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {category.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {category.display_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: category.color || '#3B82F6' }}
                    />
                    <span className="text-sm text-gray-500">{category.color}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      category.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {category.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                      title="Düzenle"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Tag size={24} />
                {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori Adı (Teknik) *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                    placeholder="ornek_kategori"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Küçük harf, boşluk yerine alt çizgi kullanın
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Görünen Ad *
                </label>
                <input
                  type="text"
                  required
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                  placeholder="Örnek Kategori"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                  placeholder="Kategori açıklaması"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renk (Hex) *
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Aktif</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md transition-all duration-200"
                >
                  <Save size={18} />
                  <span>Kaydet</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default AdminNotificationCategories


